import { useState, useEffect, useRef } from "react";
import { 
  Mic,
  TrendingUp,
  TrendingDown,
  Zap,
  Radio,
  Globe,
  ArrowRight,
  Target,
  Send,
  Shield,
  Clock,
  AlertTriangle
} from "lucide-react";

// Capital Command Center - Refined
export default function CapitalCommandMockup() {
  const [commandInput, setCommandInput] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredNode, setHoveredNode] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Capital flow animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;

    // Deal nodes positioned in flow
    const nodes = [
      { id: 'TX-7842', x: 0.15, y: 0.25, stage: 'acquisition', capital: 145000, risk: 32, days: 2, name: '1247 Oak Valley Dr' },
      { id: 'TX-7846', x: 0.25, y: 0.65, stage: 'acquisition', capital: 189000, risk: 28, days: 5, name: '892 Pinewood Lane' },
      { id: 'TX-7839', x: 0.45, y: 0.35, stage: 'analysis', capital: 189000, risk: 18, days: 8, name: '456 Maple St' },
      { id: 'TX-7835', x: 0.65, y: 0.55, stage: 'execution', capital: 225000, risk: 12, days: 12, name: '2105 Elm Creek' },
      { id: 'TX-7831', x: 0.75, y: 0.25, stage: 'execution', capital: 112000, risk: 8, days: 18, name: '738 Birch Ave' },
      { id: 'TX-7828', x: 0.9, y: 0.45, stage: 'exit', capital: 135000, risk: 5, days: 24, name: '521 Pine Ridge' },
    ];

    const stageColors = {
      acquisition: '#3B82F6',
      analysis: '#8B5CF6',
      execution: '#D4AF37',
      exit: '#10B981'
    };

    // Particle system for capital flow
    let particles = [];
    const createParticle = () => {
      const startNode = nodes[Math.floor(Math.random() * (nodes.length - 1))];
      const endIdx = nodes.findIndex(n => n.id === startNode.id) + 1;
      if (endIdx >= nodes.length) return null;
      const endNode = nodes[endIdx];
      return {
        x: startNode.x * width,
        y: startNode.y * height,
        targetX: endNode.x * width,
        targetY: endNode.y * height,
        progress: 0,
        speed: 0.003 + Math.random() * 0.002,
        color: stageColors[startNode.stage]
      };
    };

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw connection lines
      ctx.strokeStyle = 'rgba(212, 175, 55, 0.08)';
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length - 1; i++) {
        const start = nodes[i];
        const end = nodes[i + 1];
        ctx.beginPath();
        const cp1x = (start.x + end.x) / 2 * width;
        const cp1y = start.y * height;
        const cp2x = (start.x + end.x) / 2 * width;
        const cp2y = end.y * height;
        ctx.moveTo(start.x * width, start.y * height);
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, end.x * width, end.y * height);
        ctx.stroke();
      }

      // Update and draw particles
      if (Math.random() < 0.02 && particles.length < 15) {
        const p = createParticle();
        if (p) particles.push(p);
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const t = p.progress;
        const x = p.x + (p.targetX - p.x) * t;
        const y = p.y + (p.targetY - p.y) * t;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.6 * (1 - t);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      // Draw nodes
      nodes.forEach((node, idx) => {
        const x = node.x * width;
        const y = node.y * height;
        const color = stageColors[node.stage];
        const isHovered = hoveredNode === node.id;
        
        // Outer pulse
        const pulse = Math.sin(Date.now() / 1000 + idx) * 0.3 + 0.7;
        ctx.beginPath();
        ctx.arc(x, y, isHovered ? 24 : 18 * pulse, 0, Math.PI * 2);
        ctx.fillStyle = `${color}15`;
        ctx.fill();

        // Inner glow
        ctx.beginPath();
        ctx.arc(x, y, isHovered ? 14 : 10, 0, Math.PI * 2);
        ctx.fillStyle = `${color}40`;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(x, y, isHovered ? 8 : 5, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [hoveredNode]);

  // Transaction flow stages with enhanced data
  const flowStages = [
    { stage: "ACQUISITION", count: 2, value: 334000, avgDays: 3.5, velocity: 'up', color: "#3B82F6" },
    { stage: "ANALYSIS", count: 1, value: 189000, avgDays: 8, velocity: 'down', color: "#8B5CF6" },
    { stage: "EXECUTION", count: 2, value: 337000, avgDays: 15, velocity: 'up', color: "#D4AF37" },
    { stage: "EXIT", count: 1, value: 135000, avgDays: 6, velocity: 'up', color: "#10B981" },
  ];

  // Active transactions with enhanced data
  const transactions = [
    { id: 'TX-7842', address: '1247 Oak Valley Dr', stage: 'ACQUISITION', value: 145000, spread: 48000, daysInStage: 2, capitalRequired: 145000, riskScore: 32, aiConfidence: 78, stageColor: '#3B82F6' },
    { id: 'TX-7839', address: '892 Pinewood Lane', stage: 'ANALYSIS', value: 189000, spread: 66000, daysInStage: 8, capitalRequired: 189000, riskScore: 18, aiConfidence: 82, stageColor: '#8B5CF6' },
    { id: 'TX-7835', address: '456 Maple Street', stage: 'EXECUTION', value: 112000, spread: 48000, daysInStage: 12, capitalRequired: 112000, riskScore: 12, aiConfidence: 91, stageColor: '#D4AF37', active: true },
    { id: 'TX-7831', address: '2105 Elm Creek Ct', stage: 'EXECUTION', value: 225000, spread: 82000, daysInStage: 18, capitalRequired: 225000, riskScore: 8, aiConfidence: 88, stageColor: '#D4AF37' },
    { id: 'TX-7828', address: '738 Birch Avenue', stage: 'EXIT', value: 135000, spread: 47000, daysInStage: 24, capitalRequired: 0, riskScore: 5, aiConfidence: 95, stageColor: '#10B981' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]/80 backdrop-blur-sm text-white overflow-hidden relative">
      {/* Ambient Background - Subtle */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[1000px] h-[1000px] bg-[#D4AF37]/[0.02] rounded-full blur-[200px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[800px] h-[800px] bg-blue-500/[0.02] rounded-full blur-[200px]" />
      </div>

      {/* Top System Strip - Enhanced */}
      <header className="relative z-10 border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm/90 backdrop-blur-2xl">
        <div className="flex items-center justify-between px-8 py-4">
          {/* Logo & Live Status */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-[#D4AF37] to-[#8B6914] rounded flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
                <Target className="w-5 h-5 text-black" />
              </div>
              <div>
                <span className="text-lg font-semibold tracking-tight">
                  DEAL<span className="text-[#D4AF37]">FORGE</span>
                </span>
                <div className="text-[9px] text-white/30 uppercase tracking-[0.2em] -mt-0.5">Command Center</div>
              </div>
            </div>
            
            {/* Live Indicator - Enhanced */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                <div className="relative">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                  <div className="absolute inset-0 w-2 h-2 bg-emerald-400 rounded-full animate-ping opacity-75" />
                </div>
                <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] rounded">
                <Shield className="w-3 h-3 text-white/40" />
                <span className="text-[10px] text-white/40 uppercase tracking-wider">System Stable</span>
              </div>
            </div>
          </div>

          {/* Live Metrics - Enhanced Typography */}
          <div className="flex items-center gap-10">
            <MetricDisplay 
              label="CAPITAL DEPLOYED" 
              value="$2.85M"
              trend="+12.4%"
              positive
            />
            <div className="w-px h-8 bg-white/[0.06]" />
            <MetricDisplay 
              label="INFLOW (30D)" 
              value="$485K"
              trend="+$85K"
              positive
            />
            <div className="w-px h-8 bg-white/[0.06]" />
            <MetricDisplay 
              label="VELOCITY" 
              value="12.4d"
              trend="-2.1"
              positive
            />
            <div className="w-px h-8 bg-white/[0.06]" />
            <MetricDisplay 
              label="ACTIVE" 
              value="5"
              highlight
            />
          </div>

          {/* Time */}
          <div className="text-right">
            <div className="font-mono text-lg text-white/70 tracking-tight">
              {currentTime.toLocaleTimeString('en-US', { hour12: false })}
            </div>
            <div className="text-[9px] text-white/30 uppercase tracking-wider">Eastern</div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 p-6 h-[calc(100vh-90px)]">
        <div className="grid grid-cols-12 gap-5 h-full">
          
          {/* Left Panel - Transaction Flow Enhanced */}
          <div className="col-span-3 flex flex-col gap-5">
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">Transaction Flow</h3>
                  <p className="text-[10px] text-white/30 mt-0.5">Capital movement by stage</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-light text-white font-mono">$995K</div>
                  <div className="text-[9px] text-white/40">Total Active</div>
                </div>
              </div>
              
              {/* Flow Visualization - Enhanced */}
              <div className="space-y-4">
                {flowStages.map((stage, idx) => (
                  <div key={stage.stage} className="group">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-white/70 uppercase tracking-wider">{stage.stage}</span>
                        <span className="text-[10px] font-mono font-bold text-white/30">{stage.count}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-white/30" />
                          <span className="text-[10px] font-mono font-bold text-white/40">{stage.avgDays}d avg</span>
                        </div>
                        {stage.velocity === 'up' ? (
                          <TrendingUp className="w-3 h-3 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                        ) : (
                          <TrendingDown className="w-3 h-3 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
                        )}
                      </div>
                    </div>
                    <div className="h-10 bg-white/[0.03] rounded overflow-hidden relative">
                      <div 
                        className="h-full rounded transition-all duration-700 relative overflow-hidden"
                        style={{ 
                          width: `${(stage.value / 400000) * 100}%`,
                          background: `linear-gradient(90deg, ${stage.color}30, ${stage.color}10)`
                        }}
                      >
                        {/* Animated shimmer */}
                        <div 
                          className="absolute inset-0 animate-shimmer"
                          style={{ 
                            background: `linear-gradient(90deg, transparent, ${stage.color}20, transparent)`,
                            backgroundSize: '200% 100%'
                          }}
                        />
                      </div>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <span className="font-mono text-sm text-white/90">${(stage.value / 1000).toFixed(0)}K</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Deploy - Refined */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 backdrop-blur-sm flex-1">
              <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">Quick Deploy</h3>
              <div className="space-y-2">
                <QuickAction icon={Target} label="New Acquisition" />
                <QuickAction icon={Zap} label="Run Analysis" />
                <QuickAction icon={Send} label="Execute Offer" />
                <QuickAction icon={Globe} label="Market Scan" />
              </div>
            </div>
          </div>

          {/* Center Panel - Capital Flow Visualization */}
          <div className="col-span-6 flex flex-col gap-5">
            {/* AI Command Bar */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4 backdrop-blur-sm">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={commandInput}
                    onChange={(e) => setCommandInput(e.target.value)}
                    placeholder="Command your capital..."
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-2xl px-5 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/30 focus:bg-white/[0.04] transition-all"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-white/[0.05] border border-white/[0.1] rounded text-[10px] font-mono font-bold text-white/40">⌘K</kbd>
                  </div>
                </div>
                <button className="w-11 h-11 bg-white/[0.03] border border-white/[0.06] rounded-2xl flex items-center justify-center hover:bg-white/[0.06] hover:border-[#D4AF37]/20 transition-all">
                  <Mic className="w-4 h-4 text-white/40" />
                </button>
              </div>
            </div>

            {/* Capital Flow Canvas */}
            <div className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden relative">
              <canvas 
                ref={canvasRef} 
                className="absolute inset-0 w-full h-full"
              />
              
              {/* Hover Tooltip */}
              {hoveredNode && (
                <div className="absolute bg-black/90 border border-white/10 rounded-2xl p-3 pointer-events-none z-10"
                  style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
                  <div className="text-xs text-white/50 mb-1">Deal Details</div>
                  <div className="text-sm text-white font-medium">Loading...</div>
                </div>
              )}

              {/* Stage Legend */}
              <div className="absolute bottom-4 left-4 flex items-center gap-4 bg-black/40 backdrop-blur-sm border border-white/[0.06] rounded-2xl px-4 py-2">
                {['Acquisition', 'Analysis', 'Execution', 'Exit'].map((stage, idx) => (
                  <div key={stage} className="flex items-center gap-2">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: ['#3B82F6', '#8B5CF6', '#D4AF37', '#10B981'][idx] }}
                    />
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">{stage}</span>
                  </div>
                ))}
              </div>

              {/* Capital Summary */}
              <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4 text-right">
                <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Capital in Motion</div>
                <div className="text-3xl font-light text-white font-mono tracking-tight">
                  $2.84<span className="text-[#D4AF37]">M</span>
                </div>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                  <span className="text-[11px] text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-mono">+12.4% MTD</span>
                </div>
              </div>

              {/* Flow Direction Indicator */}
              <div className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm border border-white/[0.06] rounded-2xl px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <div className="w-1 h-1 bg-[#D4AF37] rounded-full animate-pulse" />
                    <div className="w-1 h-1 bg-[#D4AF37]/60 rounded-full animate-pulse delay-100" />
                    <div className="w-1 h-1 bg-[#D4AF37]/30 rounded-full animate-pulse delay-200" />
                    <ArrowRight className="w-3 h-3 text-[#D4AF37]/60 ml-1" />
                  </div>
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">Capital Flow</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Active Transactions Enhanced */}
          <div className="col-span-3 flex flex-col gap-5">
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 backdrop-blur-sm flex-1 overflow-hidden">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">Active Transactions</h3>
                  <p className="text-[10px] text-white/30 mt-0.5">Live capital deployment</p>
                </div>
                <div className="flex items-center gap-2 px-2.5 py-1 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded">
                  <div className="relative">
                    <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
                    <div className="absolute inset-0 w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-ping opacity-50" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#D4AF37]">5 LIVE</span>
                </div>
              </div>
              
              <div className="space-y-3 overflow-y-auto max-h-[calc(100%-60px)] pr-1">
                {transactions.map((tx) => (
                  <TransactionCard key={tx.id} {...tx} />
                ))}
              </div>
            </div>

            {/* System Status - Refined */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">System Status</h3>
                <Radio className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              </div>
              <div className="space-y-2.5">
                <StatusRow label="AI Engine" status="operational" latency="12ms" />
                <StatusRow label="Market Data" status="operational" latency="45ms" />
                <StatusRow label="Integrations" status="operational" latency="23ms" />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Status - Minimal */}
      <footer className="fixed bottom-0 left-0 right-0 h-8 bg-[#0A0A0A]/80 backdrop-blur-xl border-t border-white/[0.06] flex items-center justify-between px-8 z-10">
        <div className="flex items-center gap-6 text-[10px] text-white/30">
          <span>Session: <span className="text-white/50 font-mono">4h 23m</span></span>
          <span>Last sync: <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-mono">2s</span></span>
        </div>
        <div className="text-[10px] text-white/30">
          DEALFORGE CAPITAL COMMAND <span className="text-white/20 mx-2">·</span> <span className="font-mono">v3.0.0</span>
        </div>
      </footer>

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

// Enhanced Metric Display
function MetricDisplay({ label, value, trend, positive, highlight }) {
  return (
    <div>
      <div className="text-[9px] font-medium text-white/40 uppercase tracking-[0.15em] mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-light tracking-tight ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>
          {value}
        </span>
        {trend && (
          <span className={`flex items-center gap-0.5 text-[11px] font-mono ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

// Quick Action - Refined
function QuickAction({ icon: Icon, label }) {
  return (
    <button className="w-full flex items-center gap-3 px-4 py-3 bg-white/[0.02] hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] border border-white/[0.06] hover:border-[#D4AF37]/20 rounded-2xl transition-all duration-200 group">
      <Icon className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-all duration-300" />
      <span className="text-sm text-white/50 group-hover:text-white/90 transition-all duration-300">{label}</span>
      <ArrowRight className="w-3 h-3 text-white/30 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
    </button>
  );
}

// Transaction Card - Enhanced as Live Engine
function TransactionCard({ id, address, stage, value, spread, daysInStage, capitalRequired, riskScore, aiConfidence, stageColor, active }) {
  return (
    <div className={`relative p-4 bg-white/[0.02] hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] rounded-2xl cursor-pointer transition-all duration-200 group ${active ? 'border border-[#D4AF37]/20' : 'border border-transparent hover:border-white/[0.06]'}`}>
      {/* Live Pulse for Active */}
      {active && (
        <div className="absolute top-3 right-3">
          <div className="relative">
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
            <div className="absolute inset-0 w-2 h-2 bg-[#D4AF37] rounded-full animate-ping opacity-50" />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span 
          className="px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider"
          style={{ color: stageColor, background: `${stageColor}15` }}
        >
          {stage}
        </span>
        <div className="flex items-center gap-1 text-[10px] text-white/40">
          <Clock className="w-3 h-3" />
          <span className="font-mono">{daysInStage}d</span>
        </div>
      </div>

      {/* Address */}
      <div className="text-sm text-white mb-3 truncate group-hover:text-white">{address}</div>

      {/* Metrics Row */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[9px] text-white/30 uppercase tracking-wider">Capital</div>
          <div className="text-sm font-mono font-bold text-white/70">${(value / 1000).toFixed(0)}K</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] text-white/30 uppercase tracking-wider">Spread</div>
          <div className="text-sm font-mono text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">+${(spread / 1000).toFixed(0)}K</div>
        </div>
      </div>

      {/* Intelligence Row */}
      <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Risk Score */}
          <div className="flex items-center gap-1.5">
            <AlertTriangle className={`w-3 h-3 ${riskScore > 25 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]'}`} />
            <span className="text-[10px] font-mono font-bold text-white/50">{riskScore}</span>
          </div>
          {/* AI Confidence */}
          <div className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[10px] font-mono font-bold text-white/50">{aiConfidence}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Status Row - Refined
function StatusRow({ label, status, latency }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-[11px] text-white/50">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-mono font-bold text-white/30">{latency}</span>
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${status === 'operational' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
          <span className={`text-[9px] font-mono uppercase ${status === 'operational' ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}`}>
            {status === 'operational' ? 'OK' : status}
          </span>
        </div>
      </div>
    </div>
  );
}
